import React, { useState } from 'react'
import { t } from 'i18next'

import Accordion from 'app/app/(client)/components/molecules/Accordion/Accordion'
import Checkbox from 'app/app/(client)/components/molecules/Checkbox/Checkbox'
import IconwithText from 'app/app/(client)/components/molecules/IconwithText/IconwithText'
import Button from 'app/app/(client)/components/atoms/Button/Button'
import Divider from 'app/app/(client)/components/atoms/Divider/Divider'
import Icon from 'app/app/(client)/components/atoms/Icon/Icon'
import Text from 'app/app/(client)/components/atoms/Text/Text'
import Opportunity from 'app/app/(client)/components/templates/Opportunity/Opportunity'
import { useAppDispatch, useAppSelector } from 'app/app/(client)/hooks/redux-hook'
import { addAllOpportunityIds, removeAllOpportunityIds } from '../../../redux/features/customers/opportunity-slice'
import { deleteOpportunities } from 'app/app/(client)/utils/opportunity-utils'
import { capitalizeIfEnglish } from 'app/app/(client)/utils/utils'
import { CustomerModel, OpportunityModel } from 'types/ui/opportunity'

export const Customer: React.FC<CustomerModel> = ({
  id,
  name,
  numberOfContracts = 0,
  opportunities = [] as OpportunityModel[]
}) => {
  const dispatch = useAppDispatch()
  const { selectedForUnShare, showShares } = useAppSelector(state => state.opportunities)
  const customerRowCheckboxChecked = opportunities.every(opportunity => selectedForUnShare.includes(opportunity.id))
  const [open, setOpen] = useState(false)
  const opportunityText =
    opportunities.length > 1
      ? capitalizeIfEnglish(t('customers.opportunities'))
      : capitalizeIfEnglish(t('customers.opportunity'))
  const contractsText =
    numberOfContracts > 1
      ? capitalizeIfEnglish(t('customers.refresh.contracts'))
      : capitalizeIfEnglish(t('customers.refresh.contract'))

  const handleCustomerCheckboxChange = (
    event: React.MouseEvent<HTMLDivElement | HTMLButtonElement>,
    isChecked: boolean
  ) => {
    event.stopPropagation()

    const opportunityIds: string[] = opportunities.map(opportunity => opportunity.id)

    if (isChecked) {
      dispatch(addAllOpportunityIds(opportunityIds))
    } else {
      dispatch(removeAllOpportunityIds(opportunityIds))
    }
  }

  return (
    <Accordion
      id={`customer-accordion-${id}`}
      open={open}
      setOpen={setOpen}
      summaryOverlayHeader={
        <>
          <div className="grid grid-cols-2 px-2 text-14px font-normal">
            <div className="col-span-2 ">
              <div className="flex w-full items-center gap-[24px]">
                <Button
                  id={`mobile-overlay-back-button-${id}`}
                  variant="tertiary"
                  size="small"
                  className="btn-icon flex border-1 border-primary"
                  onClick={() => setOpen(false)}
                >
                  <Icon name="icon-arrow-left" />
                </Button>

                <Text id={`mobile-overlay-customer-name-${id}`} as="div" className="text-28px ">
                  {name}
                </Text>
              </div>
            </div>
            <div className="col-span-1 flex items-center gap-1 mt-2">
              <IconwithText
                id={`mobile-overlay-opportunity-count-${id}`}
                iconName={`icon-new_release_2`}
                textContent={[{ text: `${opportunities.length.toString()} ${capitalizeIfEnglish(opportunityText)}` }]}
              />
            </div>
            <div className="col-span-1 flex items-center gap-1 mt-2">
              <IconwithText
                id={`mobile-overlay-contract-count-${id}`}
                iconName={`icon-note`}
                textContent={[{ text: `${numberOfContracts} ${capitalizeIfEnglish(contractsText)}` }]}
              />
            </div>
            {!showShares && (
              <div className="col-span-2 flex items-center gap-3 mt-2 w-full">
                <Button
                  id={`mobile-overlay-select-all-button-${id}`}
                  onClick={e => handleCustomerCheckboxChange(e, !customerRowCheckboxChecked)}
                  variant="tertiary"
                  size="small"
                  className="btn h-9 flex items-center text-center justify-center order-1 text-[16px] font-semibold leading-6 w-1/2"
                >
                  {customerRowCheckboxChecked ? t('customers.refresh.deselectAll') : t('customers.refresh.selectAll')}
                </Button>
                <Button
                  id={`mobile-overlay-delete-button-${id}`}
                  onClick={() => deleteOpportunities(selectedForUnShare, dispatch)}
                  variant="tertiary"
                  size="small"
                  className="btn h-9 flex items-center justify-center order-1 w-1/2"
                  disabled={selectedForUnShare.length === 0}
                >
                  <IconwithText
                    id={`mobile-overlay-delete-IconwithText-${id}`}
                    iconName={`icon-delete text-18px py-[2px]`}
                    textContent={[{ text: t('customers.refresh.delete') }]}
                  />
                </Button>
              </div>
            )}
          </div>
          <Divider orientation="horizontal" className="divider mt-[16px]" />
        </>
      }
      summary={
        <div className="w-full grid grid-cols-2 md:grid-cols-12 lg:grid-cols-12 gap-[24px] mr-[24px]">
          <div className="col-span-2 md:col-span-7 lg:col-span-8 text-16px text-left flex gap-[24px] items-center font-bold">
            {!showShares && (
              <Checkbox
                id={`summary-customer-checkbox-${id}`}
                checked={customerRowCheckboxChecked}
                onChecked={(e, checked) => handleCustomerCheckboxChange(e, checked)}
              />
            )}
            <Text id={`summary-customer-name-${id}`}>{name}</Text>
          </div>

          <div className="hidden md:block md:col-span-3 lg:col-span-2">
            <IconwithText
              id={`summary-opportunity-count-${id}`}
              iconName={`icon-new_release_2 text-16px py-[2px]`}
              textContent={[{ text: `${opportunities.length.toString()} ${opportunityText}`, style: 'text-14px' }]}
            />
          </div>
          <div className="hidden md:block md:col-span-2 lg-col-span-2">
            <IconwithText
              id={`summary-contract-count-${id}`}
              iconName={`icon-note text-16px py-[2px]`}
              textContent={[{ text: `${numberOfContracts} ${contractsText}`, style: 'text-14px' }]}
            />
          </div>
        </div>
      }
      summaryFooter={
        <div className="grid grid-cols-2 md:hidden mt-[12px]">
          <div className="col-span-1">
            <IconwithText
              id={`summaryfooter-opportunity-count-${id}`}
              iconName={`icon-new_release_2 text-16px py-[2px]`}
              textContent={[{ text: `${opportunities.length.toString()} ${opportunityText}` }]}
            />
          </div>
          <div className="col-span-1 items-">
            <IconwithText
              id={`summaryfooter-contract-count-${id}`}
              iconName={`icon-note text-16px py-[2px]`}
              textContent={[{ text: `${numberOfContracts} ${contractsText}` }]}
            />
          </div>
        </div>
      }
      className="w-full rounded-[16px] border-[0.5px] border-border-card bg-white shadow-small cursor-pointer"
      fullScreenOverlay={true}
    >
      <div className="flex flex-col px-0 mt-3 md:mt-0 md:px-6 mb-6 gap-y-3 md:gap-y-6">
        {opportunities.map((opportunityDetails: OpportunityModel, idx: number) => (
          <Opportunity key={idx} {...opportunityDetails} />
        ))}
      </div>
    </Accordion>
  )
}
